import { TargetNodeType } from "@p0security/shared/types/assessment/data/index";
import { RoutingRule } from "@p0security/shared/types/workflow/types";

export type GovernanceAppliesTo = "agents" | "mcp-servers";

export type GovernanceViolator = {
  id: string;
  name: string;
  platform: string;
  source: string;
  discoveredAt: string;
};

export type GovernancePolicy = {
  id: string;
  name: string;
  description: string;
  appliesTo: GovernanceAppliesTo;
  appliesToLabel: string;
  enabled: boolean;
  enforcedCount: number;
  query: GovernancePolicyQuery;
  violators: GovernanceViolator[];
  /** Label for the inline remediation button on each violator. */
  remediationLabel: string;
};

export type GovernancePolicyQuery = {
  show: TargetNodeType;
  where: string;
};

export const GOVERNANCE_POLICIES: GovernancePolicy[] = [
  {
    appliesTo: "agents",
    appliesToLabel: "All agents in scope",
    description:
      "Each discovered agent must be tied to a human responsible for it.",
    enabled: true,
    enforcedCount: 7,
    id: "agent-must-have-owner",
    name: "Every agent must have an owner",
    query: {
      show: "identity",
      where: 'identity={owner:"true" type:"agent"}',
    },
    remediationLabel: "Assign owner",
    violators: [],
  },
  {
    appliesTo: "mcp-servers",
    appliesToLabel: "All MCP servers",
    description:
      "Critical MCP servers should be brokered through P0 rather than accessed directly.",
    enabled: true,
    enforcedCount: 2,
    id: "mcp-server-must-be-managed",
    name: "Every MCP server must be managed by P0",
    query: {
      show: "resource",
      where: 'resource={type:"mcp-server" gateway:"p0"}',
    },
    remediationLabel: "Manage with P0",
    violators: [
      {
        discoveredAt: "2 days ago",
        id: "github-mcp-direct",
        name: "github-mcp",
        platform: "Direct",
        source: "Discovered via Copilot",
      },
      {
        discoveredAt: "5 days ago",
        id: "linear-mcp-direct",
        name: "linear-mcp",
        platform: "Direct",
        source: "Discovered via Copilot",
      },
    ],
  },
  {
    appliesTo: "agents",
    appliesToLabel: "All agents in scope",
    description: "Agent identities must rotate within the configured window.",
    enabled: true,
    enforcedCount: 4,
    id: "no-long-lived-credentials",
    name: "No long-lived agent credentials",
    query: {
      show: "credential",
      where:
        'credential={stale90:"true" type:"key"}->identity={type:"agent" status:"active"}',
    },
    remediationLabel: "Rotate now",
    violators: [],
  },
];

export type ControlRow = {
  readonly id: string;
  readonly kind: "control";
  readonly rule: RoutingRule;
};

export type GovernanceRow = {
  readonly id: string;
  readonly kind: "governance";
  readonly policy: GovernancePolicy;
};

export type PolicyRow = ControlRow | GovernanceRow;

export const isGovernanceRow = (row: PolicyRow): row is GovernanceRow =>
  row.kind === "governance";

export const isControlRow = (row: PolicyRow): row is ControlRow =>
  row.kind === "control";

/**
 * Returns display-ready compliance metrics from the policy's current counts.
 */
export const getGovernancePolicyStats = (policy: GovernancePolicy) => {
  const totalCount = policy.enforcedCount + policy.violators.length;
  const compliance = totalCount === 0 ? 1 : policy.enforcedCount / totalCount;

  return {
    compliance,
    compliancePercent: Math.round(compliance * 100),
    ratio: `${policy.enforcedCount}/${totalCount}`,
    totalCount,
  };
};

/**
 * Moves one violating entity into the policy's enforced count.
 */
export const remediateGovernanceViolator = (
  policies: GovernancePolicy[],
  policyId: string,
  violatorId: string
) =>
  policies.map((policy) => {
    if (policy.id !== policyId) return policy;
    if (!policy.violators.some((violator) => violator.id === violatorId)) {
      return policy;
    }

    return {
      ...policy,
      enforcedCount: policy.enforcedCount + 1,
      violators: policy.violators.filter(
        (violator) => violator.id !== violatorId
      ),
    };
  });
